import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { track } from "../lib/analytics";
import { TrendingUp, MessageSquare, Users, BarChart3, CheckCircle2, Sparkles, ArrowRight } from "lucide-react";
import { Eyebrow } from "../components/Bits";

const BICONS = [Users, MessageSquare, TrendingUp, BarChart3];

export default function Partners() {
  const { t } = useLang();
  const cats = t("partners.cats");
  const F = t("partners.fields");
  const [form, setForm] = useState({ name: "", company: "", category: cats[0], email: "", phone: "", website: "", areas: "", interest: "", company_website: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/partners/apply", form);
      setSent(true);
      track("partner_form_completed", { category: form.category });
      toast.success(t("partners.success_p"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error"));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-14" data-testid="partners-page">
      <Eyebrow>{t("partners.eyebrow")}</Eyebrow>
      <h1 className="font-display text-4xl sm:text-5xl text-white mt-2">{t("partners.title")}</h1>
      <p className="text-white/60 mt-3 max-w-2xl">{t("partners.sub")}</p>
      <Link to="/partners/broker" data-testid="broker-cta" className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full border border-gold/30 text-gold hover:bg-gold/10 transition-colors duration-200 text-sm">
        {t("partners.broker_cta")} <ArrowRight size={15} />
      </Link>

      <div className="grid md:grid-cols-4 gap-4 mt-10">
        {t("partners.benefits").map((b, i) => {
          const Icon = BICONS[i] || Users;
          return (
            <div key={b.h} className="rounded-2xl bg-surface/60 border border-white/[0.06] p-6">
              <div className="w-10 h-10 rounded-lg bg-gold/10 text-gold flex items-center justify-center"><Icon size={18} /></div>
              <div className="font-display text-lg text-white mt-3">{b.h}</div>
              <div className="text-white/55 text-sm mt-1">{b.p}</div>
            </div>
          );
        })}
      </div>

      {/* AI employees */}
      <div className="glass rounded-2xl p-7 mt-8">
        <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-wider mb-4"><Sparkles size={13} /> {t("partners.ai_h")}</div>
        <div className="flex flex-wrap gap-2">
          {t("partners.ai").map((a) => <span key={a} className="text-sm px-3 py-1.5 rounded-full bg-white/[0.03] border border-white/10 text-white/70">{a}</span>)}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-8 items-start">
        <div className="glass rounded-2xl p-7">
          <h2 className="font-display text-2xl text-white mb-4">{t("partners.form_h")}</h2>
          {sent ? (
            <div className="flex flex-col items-center text-center py-10" data-testid="partner-success">
              <CheckCircle2 size={40} className="text-emerald-400" />
              <div className="text-white font-medium mt-3">{t("partners.success_h")}</div>
              <p className="text-white/55 text-sm mt-1">{t("partners.success_p")}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="partner-form">
              <input type="text" value={form.company_website} onChange={set("company_website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder={F.name} value={form.name} onChange={set("name")} data-testid="partner-name" className="bg-input/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-gold/40" />
                <input required placeholder={F.company} value={form.company} onChange={set("company")} data-testid="partner-company" className="bg-input/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-gold/40" />
              </div>
              <select value={form.category} onChange={set("category")} data-testid="partner-category" className="w-full bg-input/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-gold/40">
                {cats.map((c) => <option key={c} className="bg-surface">{c}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input required type="email" placeholder={F.email} value={form.email} onChange={set("email")} data-testid="partner-email" className="bg-input/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-gold/40" />
                <input required placeholder={F.phone} value={form.phone} onChange={set("phone")} data-testid="partner-phone" className="bg-input/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-gold/40" />
              </div>
              <input placeholder={F.website} value={form.website} onChange={set("website")} data-testid="partner-website" className="w-full bg-input/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-gold/40" />
              <input placeholder={F.areas} value={form.areas} onChange={set("areas")} data-testid="partner-areas" className="w-full bg-input/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-gold/40" />
              <textarea placeholder={F.interest} value={form.interest} onChange={set("interest")} rows={3} data-testid="partner-interest" className="w-full bg-input/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-gold/40 resize-none" />
              <button disabled={busy} type="submit" data-testid="partner-submit" className="w-full py-3 rounded-lg bg-gold text-ink font-semibold hover:brightness-110 disabled:opacity-50 transition-all duration-200">
                {busy ? t("common.submitting") : t("common.submit")}
              </button>
            </form>
          )}
        </div>

        <div className="glass rounded-2xl p-7">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-2xl text-white">{t("partners.dash_h")}</h2>
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-gold/15 text-gold border border-gold/20">{t("partners.dash_preview")}</span>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            {t("partners.dash_stats").map(([k, v]) => (
              <div key={k} className="rounded-xl bg-white/[0.03] border border-white/5 p-4">
                <div className="font-display text-2xl text-gold">{v}</div>
                <div className="text-[11px] text-white/50 mt-1">{k}</div>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            {["AC repair · Dubai Marina", "2BR viewing · Downtown", "Moving quote · JVC"].map((r, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: 10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="flex items-center justify-between rounded-lg bg-white/[0.02] px-4 py-3 text-sm text-white/70">
                <span>{r}</span>
                <span className={`text-xs ${i === 1 ? "text-emerald-400" : "text-gold"}`}>{i === 1 ? "Replied" : "New"}</span>
              </motion.div>
            ))}
          </div>
          <p className="text-[11px] text-white/40 mt-4">{t("partners.dash_note")}</p>
        </div>
      </div>
    </div>
  );
}
